/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slice/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/report';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Custom square bar component
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;
  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => { }}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}

/**
 *
 * Maintenance ReportRevenue component
 */
function MaintenanceReportRevenue() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label || data?.stationName}</p>
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('inspection')}: {data?.inspectionCount}
            </p>
            <p style={{ color: '#2E9E3A' }}>
              {t('repair')}: {data?.repairCount}
            </p>
            <p style={{ color: '#E41010' }}>
              {t('softwareUpdate')}: {data?.softwareUpdateCount}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'maintenanceScheduleTask',
      });
      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('maintenanceScheduleStatus') ? 'active' : ''}`}
              onClick={() => handleClick('maintenanceScheduleStatus')}>
              {reportsData.activePinName.includes('maintenanceScheduleStatus') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>

            <p>{t('maintenanceTask')}</p>
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration: { label: any }) => t(duration.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) fetchPowerConsumedReport(selected.value);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData}>
              <XAxis
                dataKey="label" // Assuming 'label' has 'Jan', 'Feb', etc.
                axisLine
                tickLine={false}
                tick={{ fontSize: 12 }}
              />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar
                dataKey="inspectionCount"
                name={t('basePower')}
                fill="#4ABDFB"
                shape={<TriangleBar />}
                barSize={5}
              />
              <Bar
                dataKey="repairCount"
                name={t('offPeakPower')}
                fill="#2E9E3A"
                shape={<TriangleBar />}
                barSize={5}
              />
              <Bar
                dataKey="softwareUpdateCount"
                name={t('peakPower')}
                fill="#E41010"
                shape={<TriangleBar />}
                barSize={5}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#4ABDFB' }} />
          <p>{t('inspection')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
          <p>{t('repair')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#E41010' }} />
          <p>{t('softwareUpdate')}</p>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceReportRevenue;
